import { useState } from 'react';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { layout, radius } from '@/theme';

import { FeedFilters } from './FeedFilters';
import { activeFilterCount } from './filters';
import type { FeedFiltersApi } from './useFeedFilters';

/**
 * Filters on narrow screens (spec §9.3): a sticky bar that opens a bottom
 * sheet.
 *
 * The panel used to sit expanded above the feed, which meant scrolling past a
 * screenful of controls to reach the first post on a phone. The bar keeps the
 * controls one tap away and says how many are already on, so "Filters" is never
 * a button whose state you have to open it to learn.
 */
export function FeedFilterBar({ api }: { api: FeedFiltersApi }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const count = activeFilterCount(api.filters);
  const place = api.filters.town || (api.filters.country ? api.filters.country : t('feed.global'));

  return (
    <>
      <Box
        sx={{
          display: { xs: 'flex', lg: 'none' },
          position: 'sticky',
          top: layout.appBarHeight,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          alignItems: 'center',
          gap: 1,
          py: 1,
          // The bar sits over scrolling content, so it needs its own ground.
          backgroundColor: 'background.default',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={
            <Badge badgeContent={count} color="primary">
              <TuneRoundedIcon fontSize="small" />
            </Badge>
          }
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
        >
          {t('feed.filtersShort')}
        </Button>

        <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
          {place}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {count > 0 && (
          <Button size="small" variant="text" onClick={api.clear}>
            {t('common.clear')}
          </Button>
        )}
      </Box>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderTopLeftRadius: `${radius.lg}px`,
              borderTopRightRadius: `${radius.lg}px`,
              maxHeight: '85vh',
            },
          },
        }}
        aria-labelledby="feed-filters-title"
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
        >
          <Typography id="feed-filters-title" variant="h4" component="h2" sx={{ flexGrow: 1 }}>
            {t('feed.filtersShort')}
          </Typography>
          <IconButton size="small" onClick={() => setOpen(false)} aria-label={t('common.close')}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Box sx={{ p: 2, overflowY: 'auto' }}>
          <FeedFilters api={api} showHeading={false} />
        </Box>

        <Box sx={{ p: 2, pt: 0 }}>
          <Button fullWidth variant="contained" onClick={() => setOpen(false)}>
            {t('feed.showResults')}
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
